import { useEffect, useRef } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import FormControl from 'react-bootstrap/FormControl';
import { addTask as addTaskRequest, getGlobalTasks, getUnassignedTasks } from '../../services/apiClient';
import { useLegacyDidUpdate, useLegacyState } from '../../hooks/useLegacyState';
import { toErrorState } from '../../utils/errorMessage';
import type {
  ErrorState,
  LoggedUser,
  ProjectIdList,
  SelectedDate,
  StandardCode,
  UnassignedProject,
  UnassignedTaskItem,
} from '../../types';

/**
 * Port of `timeEntryRightPanelComponents/addTask.jsx`.
 *
 * Dropped dead imports (all resolve to `undefined` today and are never called): `Select`
 * from react-select, `DropdownItem` from react-bootstrap/esm (the file uses
 * `Dropdown.Item`), and `patchMethod`/`getErrorMessage`, which are replaced by the
 * apiClient equivalents.
 *
 * `class="btn btn-dark"` in the original is an invalid React prop; React warns and still
 * renders the attribute, so `className` here produces byte-identical DOM without the
 * warning. `ref={this.buttonRef}` referenced a property that was never assigned, i.e. an
 * undefined ref — dropped, as it attached nothing.
 */

interface AddTaskProps {
  /** `boolean | undefined` on purpose — see TimeEntryState.isCopyUnassigedTaskEnabled. */
  isCopyUnassigedTaskEnabled: boolean | undefined;
  addTaskResponse: (response: unknown) => void;
  callUnassignTask: boolean;
  copyUnassignTaskButton: () => void;
  deleteTaskData: unknown;
  deleteGteProjectId: number;
  refreshTimeSheetData: (selectedDate: SelectedDate, empNo: number, projId: number) => void;
  clickInput: boolean;
  projectList: unknown;
  loggedUserDetails: LoggedUser | null;
  loggedUserGteEmployeeNo: number;
  /**
   * The comma-joined project ids, or a fresh `[]` between week changes — see
   * `ProjectIdList` in state/timeEntryState.ts for why both forms are load-bearing.
   */
  projectIdList: ProjectIdList;
  operationalUserGteEmployeeNo: number;
  selectedDate: SelectedDate;
  /** Passed by timeEntryRightPanel.jsx but never read here. Kept so the parent type-checks. */
  addTask?: unknown;
  nonProjectTask: unknown;
  unassignedTasks: UnassignedProject[];
  handleSaveDraft: () => void;
}

interface AddTaskState {
  error: ErrorState;
  selectProjectType: string;
  temp: { key: number };
  globalTasks: unknown[];
  selectedProjectTasks: UnassignedProject | undefined;
  searchValue: string;
  searchTaskValue: string;
  unassignedProjects: UnassignedProject[];
  selectedProject: { value: number; label: string };
  selectedTask: unknown[];
  options: unknown[];
  selectedOption: UnassignedProject;
  selectedOptionNonproject: StandardCode;
  taskName: string;
  selectedUnassignedTask: UnassignedTaskItem;
  disable: boolean;
  nonProjectTask: StandardCode[] | null;
  disableNonProjectTaskBtn: boolean;
  sameUser: boolean;
  disableNonProject: boolean;
  isCopyUnassigedTaskEnabled: boolean;
  /** Written in componentDidMount, never read. The misspelling is the original's. */
  isDissable?: boolean;
  /** Written by every catch block, never read. */
  loading?: boolean;
}

export function AddTask(props: AddTaskProps) {
  /*
   * `selectedProject`, `selectedTask`, `selectedOption`, `selectedUnassignedTask` and
   * `selectedOptionNonproject` all start as `[]` or `{}` in the original — the wrong shape
   * for what they later hold. The casts keep those exact runtime values rather than
   * substituting `undefined`, because `selectedOptionNonproject?.hasOwnProperty(...)` in
   * render depends on `{}` being an object (it renders "Not Applicable"), and
   * `checkUnassignedTaskIsEmpty` depends on `[]` NOT being undefined.
   */
  const { state, stateRef, setState } = useLegacyState<AddTaskState>(() => ({
    error: { status: false, message: '' },
    selectProjectType: 'nonProject',
    temp: { key: 0 },
    globalTasks: [],
    selectedProjectTasks: props.unassignedTasks[0],
    searchValue: '',
    searchTaskValue: '',
    unassignedProjects: [],
    selectedProject: [] as unknown as { value: number; label: string },
    selectedTask: [],
    options: [],
    selectedOption: [] as unknown as UnassignedProject,
    selectedOptionNonproject: {} as StandardCode,
    taskName: '',
    selectedUnassignedTask: [] as unknown as UnassignedTaskItem,
    disable: false,
    nonProjectTask: [],
    disableNonProjectTaskBtn: true,
    sameUser: false,
    disableNonProject: false,
    isCopyUnassigedTaskEnabled: false,
  }));

  const lastRequestId = useRef<string | null>(null);

  const handleError = (error: unknown): void => {
    setState({ loading: false, error: toErrorState(error) });
  };

  const updateRequiredData = (): void => {
    setState({
      selectedOption: props.unassignedTasks[0] as UnassignedProject,
      // Not null-guarded, deliberately: `nonProjectTask` is momentarily `null` while
      // getGlobalTasks is in flight, and the original throws there too. Adding a guard
      // would change which of these two setStates wins.
      selectedOptionNonproject: (stateRef.current.nonProjectTask as StandardCode[])[0],
      unassignedProjects: props.unassignedTasks,
    });
  };

  const loadGlobalTasks = (): void => {
    const user = props.loggedUserDetails;
    if (user !== null) {
      setState({ nonProjectTask: null });

      getGlobalTasks('Y')
        .then((response) => {
          // The old code read `response?.data` (axios) and `response.data[0]` — the
          // second without optional chaining. apiGet returns the body itself, so `data`
          // here IS that array, and the asymmetric guarding is preserved.
          const data = response as unknown as StandardCode[];
          setState({
            nonProjectTask: data?.sort((a, b) =>
              a.standardCodeName > b.standardCodeName ? 1 : -1,
            ),
            selectedOptionNonproject: data[0],
          });
        })
        .catch(handleError);
    }
  };

  const setUnassignTaskEmpty = (): void => {
    setState({ unassignedProjects: [], searchTaskValue: '' });
  };

  const setUnassignedSearchValueEmpty = (): void => {
    setState({ searchValue: '', searchTaskValue: '' });
  };

  const handleUnassignedTasks = (): void => {
    setUnassignTaskEmpty();

    getUnassignedTasks(
      {
        gteEmployeeNo: props.operationalUserGteEmployeeNo,
        year: props.selectedDate.year,
        weekNo: props.selectedDate.week,
        loggedUserGteEmployeeNo: props.loggedUserGteEmployeeNo,
      },
      // `String([])` is "", which is the exact query value the old app produced by
      // concatenating an empty array into the URL.
      String(props.projectIdList),
    )
      .then((response) => {
        const unassignedTasks = (response as { unassignedTasks?: UnassignedProject[] })
          ?.unassignedTasks;
        setState({
          unassignedProjects: unassignedTasks?.sort((a, b) =>
            a.projectName > b.projectName ? 1 : -1,
          ) as UnassignedProject[],
          selectedOption: (unassignedTasks as UnassignedProject[])[0],
        });
      })
      .catch(handleError);
  };

  // componentDidMount
  useEffect(() => {
    updateRequiredData();
    loadGlobalTasks();
    setState({
      isDissable: !props.clickInput,
      isCopyUnassigedTaskEnabled: props.isCopyUnassigedTaskEnabled,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLegacyDidUpdate<AddTaskProps, AddTaskState>(props, state, (prevProps, prevState) => {
    if (prevProps.isCopyUnassigedTaskEnabled !== props.isCopyUnassigedTaskEnabled) {
      setState({ isCopyUnassigedTaskEnabled: props.isCopyUnassigedTaskEnabled });
    }
    if (prevProps.projectIdList !== props.projectIdList) {
      handleUnassignedTasks();
    }
    if (prevProps.unassignedTasks !== props.unassignedTasks) {
      updateRequiredData();
    }
    if (prevProps.nonProjectTask !== props.nonProjectTask) {
      updateRequiredData();
    }
    if (prevProps.callUnassignTask !== props.callUnassignTask && props.callUnassignTask === true) {
      handleUnassignedTasks();
    }
    if (
      prevState.selectProjectType !== state.selectProjectType &&
      state.selectProjectType.toLowerCase() === 'unassigned'
    ) {
      handleUnassignedTasks();
    }
    if (prevState.selectedOption !== state.selectedOption && state.selectedOption !== undefined) {
      // `.tasks` is undefined on the initial `[]` value, so this throws if it ever runs
      // before a real option is set. Reproduced — the original has the same exposure and
      // the guard above is what keeps it from firing.
      const disable = state.selectedOption.tasks.length === 0;
      setState({ disable });
      if (state.selectedOption.tasks.length !== 0) {
        setState({ selectedUnassignedTask: state.selectedOption.tasks[0] as UnassignedTaskItem });
      }
    }
    if (prevState.taskName !== state.taskName) {
      setState({ disableNonProjectTaskBtn: state.taskName.length === 0 });
    }
    if (
      props.loggedUserGteEmployeeNo === props.operationalUserGteEmployeeNo &&
      props.loggedUserGteEmployeeNo !== 0 &&
      state.sameUser === false
    ) {
      setState({ sameUser: true, disableNonProject: false, taskName: '' });
      loadGlobalTasks();
    }

    if (props.operationalUserGteEmployeeNo !== prevProps.operationalUserGteEmployeeNo) {
      const requestId = `REQUEST-${Date.now()}`;
      lastRequestId.current = requestId;
      /*
       * Delay before handleUnassignedTasks() on a delegate-user switch: projectIdList is
       * updated by the parent's own fetch, and this waits for it. The requestId guard
       * discards a stale timer if the user switches again inside the second.
       */
      setTimeout(() => {
        if (lastRequestId.current !== requestId) {
          return;
        }
        setState({
          sameUser: false,
          disableNonProject: true,
          nonProjectTask: [],
          selectedOptionNonproject: {} as StandardCode,
          taskName: '',
        });
        handleUnassignedTasks();
      }, 1000);
    }

    if (
      props.selectedDate.dateOfFirstDayofCurrentWeek !==
      prevProps.selectedDate.dateOfFirstDayofCurrentWeek
    ) {
      setState({ taskName: '' });
      loadGlobalTasks();
      setUnassignedSearchValueEmpty();
    }

    if (prevProps.deleteTaskData !== props.deleteTaskData && props.deleteGteProjectId !== -1) {
      handleUnassignedTasks();
    }
  });

  const checkUnassignedTaskIsEmpty = (): string => {
    if (state.selectedOption !== undefined && state.selectedOption.tasks.length !== 0) {
      return state.selectedUnassignedTask?.taskName;
    }
    return 'Select an option';
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setState({ searchValue: e.target.value });
  };

  const handleSearchTaskChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setState({ searchTaskValue: e.target.value });
  };

  const handleSearchNonProjectChange = (selectedOption: StandardCode): void => {
    setState({ selectedOptionNonproject: selectedOption });
  };

  const handleOptionSelect = (option: UnassignedProject): void => {
    if (option.gteProjectId !== stateRef.current.selectedOption.gteProjectId) {
      setState({
        selectedUnassignedTask: [] as unknown as UnassignedTaskItem,
        selectedOption: option,
        searchValue: '',
      });
    }
  };

  const handleOptionSelectUnassignedTask = (option: UnassignedTaskItem): void => {
    setState({ selectedUnassignedTask: option });
  };

  const handleNonProjectSave = (): void => {
    props.handleSaveDraft();
    setState({ taskName: '' });
    const value = {
      gteProjectId: -1,
      gteEmployeeNo: props.operationalUserGteEmployeeNo,
      loggedUserGteEmployeeNo: Number(props.loggedUserGteEmployeeNo),
      year: props.selectedDate.year,
      weekNo: props.selectedDate.week,
      source: 'CheckIn Web',
      taskSource: 'SELF',
      task: {
        // Read from the ref, not the closure: the class read `this.state` here, and
        // `taskName` was set to "" two lines above without committing yet.
        taskName: stateRef.current.taskName,
        standardCode: stateRef.current.selectedOptionNonproject.standardCodeId,
      },
    };
    addTaskRequest(value)
      .then((response) => {
        props.addTaskResponse(response);
        props.refreshTimeSheetData(props.selectedDate, props.operationalUserGteEmployeeNo, -1);
      })
      .catch(handleError);
  };

  const handleUnassignedTaskSave = (): void => {
    setState({ disable: true });
    props.handleSaveDraft();
    const projectId = Number(stateRef.current.selectedOption.gteProjectId);

    const value = {
      gteProjectId: projectId,
      gteEmployeeNo: Number(props.operationalUserGteEmployeeNo),
      loggedUserGteEmployeeNo: Number(props.loggedUserGteEmployeeNo),
      year: props.selectedDate.year,
      weekNo: props.selectedDate.week,
      source: 'CheckIn Web',
      taskSource: 'SELF',
      task: {
        taskId: Number(stateRef.current.selectedUnassignedTask.taskId),
      },
    };
    addTaskRequest(value)
      .then((response) => {
        props.addTaskResponse(response);
        props.refreshTimeSheetData(
          props.selectedDate,
          props.operationalUserGteEmployeeNo,
          projectId,
        );
        handleUnassignedTasks();
        setState({ disable: false });
      })
      .catch(handleError);
  };

  const {
    searchValue,
    selectedOption,
    searchTaskValue,
    selectedOptionNonproject,
    disable,
    disableNonProjectTaskBtn,
    disableNonProject,
  } = state;

  const filteredOptions = state.unassignedProjects.filter((option) =>
    option.projectName.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const filteredTasks = selectedOption?.tasks
    ?.sort((a, b) => (a.taskName > b.taskName ? 1 : -1))
    .filter((task) => task.taskName.toLowerCase().includes(searchTaskValue.toLowerCase()));

  const nonProjectTitle = selectedOptionNonproject?.hasOwnProperty('standardCodeName')
    ? selectedOptionNonproject?.standardCodeName
    : 'Not Applicable';

  const projectTitle = selectedOption ? selectedOption.projectName : 'Select an option';

  return (
    <div>
      <div>
        <div className="btn-group project-type-btn">
          <button
            tabIndex={0}
            type="button"
            className={`btn ${state.selectProjectType === 'nonProject' ? 'active' : ''}`}
            onClick={() => setState({ selectProjectType: 'nonProject' })}
          >
            Non Project
          </button>
          <button
            tabIndex={0}
            type="button"
            className={`btn ${state.selectProjectType === 'unAssigned' ? 'active' : ''}`}
            onClick={() => setState({ selectProjectType: 'unAssigned' })}
          >
            Unassigned
          </button>
        </div>

        {state.selectProjectType === 'nonProject' && (
          <div className="ch-non-project">
            <div className="">
              <text>Non-Project Task Type :</text>
              {/* `maxLength` is not valid on a div; React renders it and App.css may key
                  off it, so it is preserved rather than dropped. */}
              <div {...{ maxLength: '100px' }}>
                <Dropdown>
                  <Dropdown.Toggle
                    tabIndex={0}
                    variant="light"
                    id="dropdown-basic"
                    disabled={disableNonProject}
                    data-toggle="tooltip"
                    data-placement="left"
                    title={nonProjectTitle}
                    {...{ alt: nonProjectTitle }}
                  >
                    {nonProjectTitle}
                  </Dropdown.Toggle>

                  <Dropdown.Menu style={{ maxHeight: '170px', overflow: 'auto' }}>
                    {state.nonProjectTask?.map((option) => (
                      <Dropdown.Item
                        key={option.standardCodeId}
                        onClick={() => handleSearchNonProjectChange(option)}
                        data-toggle="tooltip"
                        data-placement="left"
                        title={option.standardCodeName}
                        {...{ value: option.standardCodeId, alt: option.standardCodeName }}
                      >
                        {option.standardCodeName}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            <div className="">
              <text>Task Name :</text>
              <div className="col-12">
                <input
                  tabIndex={0}
                  className="col-12"
                  disabled={disableNonProject || !props.clickInput}
                  maxLength={30}
                  value={state.taskName}
                  data-toggle="tooltip"
                  data-placement="left"
                  title={state.taskName}
                  aria-label={`${state.taskName === '' ? 'Enter Task Name' : state.taskName}`}
                  type="text"
                  onChange={(e) => {
                    const newText = e.target.value.replace(/[^A-Za-z0-9\s]/g, '');
                    setState({ taskName: newText });
                  }}
                />
              </div>
              <button
                tabIndex={0}
                disabled={disableNonProjectTaskBtn || !props.clickInput}
                type="button"
                className="btn btn-dark"
                onClick={handleNonProjectSave}
                data-toggle="tooltip"
                data-placement="left"
                title="Create Non Project Task"
                {...{ alt: 'Create Non Project Task' }}
              >
                Add Task
              </button>
            </div>
            <div>
              <span className="notify-label col-12">
                (This task is valid only for the current week)
              </span>
            </div>
          </div>
        )}

        {state.selectProjectType === 'unAssigned' && state.unassignedProjects.length !== 0 && (
          <div className="ch-un-assigned">
            {state.isCopyUnassigedTaskEnabled && (
              <div className="copy-unAssign-Task">
                <button
                  tabIndex={0}
                  className=""
                  onClick={() => {
                    props.copyUnassignTaskButton();
                  }}
                  data-toggle="tooltip"
                  data-placement="left"
                  title="Copy Unassigned Task to Current Week From Previous Week"
                  {...{
                    href: '#',
                    alt: 'Copy Unassigned Task to Current Week From Previous Week',
                  }}
                >
                  Copy Self-Assigned Tasks From Previous Week
                </button>
              </div>
            )}

            <div className="">
              <text>Project Name :</text>

              <div>
                <Dropdown>
                  <Dropdown.Toggle
                    tabIndex={0}
                    variant="light"
                    id="dropdown-basic"
                    data-toggle="tooltip"
                    data-placement="left"
                    title={projectTitle}
                    {...{ alt: projectTitle }}
                  >
                    {projectTitle}
                  </Dropdown.Toggle>

                  <Dropdown.Menu style={{ maxHeight: '170px', overflow: 'auto' }}>
                    <FormControl
                      className="unassigned-task-search"
                      type="text"
                      placeholder="Search..."
                      value={searchValue}
                      onChange={handleSearchChange}
                    />
                    {filteredOptions?.map((option) => (
                      <Dropdown.Item
                        key={option.gteProjectId}
                        onClick={() => handleOptionSelect(option)}
                      >
                        {option.projectName}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            <div className="">
              <text>Task Name :</text>

              <div>
                <Dropdown>
                  <Dropdown.Toggle
                    tabIndex={0}
                    variant="light"
                    id="dropdown-basic"
                    disabled={disable}
                    data-toggle="tooltip"
                    data-placement="left"
                    title={checkUnassignedTaskIsEmpty()}
                    {...{ alt: checkUnassignedTaskIsEmpty() }}
                  >
                    {checkUnassignedTaskIsEmpty()}
                  </Dropdown.Toggle>

                  <Dropdown.Menu style={{ maxHeight: '170px', overflowY: 'auto' }}>
                    <FormControl
                      className="unassigned-task-search"
                      type="text"
                      placeholder="Search..."
                      value={searchTaskValue}
                      onChange={handleSearchTaskChange}
                    />
                    {filteredTasks?.map((option, index) => (
                      <Dropdown.Item
                        key={index}
                        onClick={() => handleOptionSelectUnassignedTask(option)}
                      >
                        {option.taskName}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <button
                tabIndex={0}
                disabled={disable || !props.clickInput}
                type="button"
                className="btn btn-dark"
                onClick={handleUnassignedTaskSave}
                data-toggle="tooltip"
                data-placement="left"
                title="Create Unassign Task"
                {...{ alt: 'Create Unassign Task' }}
              >
                Add Task
              </button>
            </div>
            <div>
              <span className="notify-label col-12">
                (This task is valid only for the current week)
              </span>
            </div>
          </div>
        )}

        {/* The empty-state twin of the block above: same markup, every control disabled. */}
        {state.selectProjectType === 'unAssigned' && state.unassignedProjects.length === 0 && (
          <div className="ch-un-assigned">
            <div className="">
              <text>Project Name :</text>

              <div>
                <Dropdown>
                  <Dropdown.Toggle
                    tabIndex={0}
                    variant="light"
                    id="dropdown-basic"
                    disabled
                    data-toggle="tooltip"
                    data-placement="left"
                    title={projectTitle}
                    {...{ alt: projectTitle }}
                  >
                    {projectTitle}
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <FormControl
                      type="text"
                      placeholder="Search..."
                      value={searchValue}
                      onChange={handleSearchChange}
                      data-toggle="tooltip"
                      data-placement="left"
                      title={projectTitle}
                      {...{ alt: projectTitle }}
                    />
                    {filteredOptions?.map((option) => (
                      <Dropdown.Item
                        key={option.gteProjectId}
                        onClick={() => handleOptionSelect(option)}
                      >
                        {option.projectName}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            <div className="">
              <text>Task Name :</text>

              <div>
                <Dropdown>
                  <Dropdown.Toggle
                    tabIndex={0}
                    variant="light"
                    id="dropdown-basic"
                    disabled
                    data-toggle="tooltip"
                    data-placement="left"
                    title={checkUnassignedTaskIsEmpty()}
                    {...{ alt: checkUnassignedTaskIsEmpty() }}
                  >
                    {checkUnassignedTaskIsEmpty()}
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <FormControl
                      type="text"
                      placeholder="Search..."
                      value={searchTaskValue}
                      onChange={handleSearchTaskChange}
                    />
                    {/* Keyed by taskId here but by index in the enabled block above —
                        the original's inconsistency, kept. */}
                    {filteredTasks?.map((option) => (
                      <Dropdown.Item
                        key={option.taskId}
                        onClick={() => handleOptionSelectUnassignedTask(option)}
                      >
                        {option.taskName}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <button
                tabIndex={0}
                disabled
                type="button"
                className="btn btn-dark"
                onClick={handleUnassignedTaskSave}
                data-toggle="tooltip"
                data-placement="left"
                title="Create Unassign Task"
                {...{ alt: 'Create Unassign Task' }}
              >
                Add Task
              </button>
            </div>
            <div>
              <span className="notify-label col-12">
                (This task is valid only for the current week)
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
